import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { useAdminApplications } from '@/features/admin/hooks/useAdminApplications'
import { adminProductionsService } from '../services/adminProductions.service'
import type { ProductionFormValues } from '../types'
import type { ProductionStatus, ProductionType } from '@/types'

const STEPS = ['Basics', 'Venue & Dates', 'Intake'] as const

const STATUS_OPTIONS: Array<{ value: ProductionStatus; label: string }> = [
  { value: 'planning', label: 'Planning' },
  { value: 'intake_open', label: 'Intake Open' },
  { value: 'curating', label: 'Curating' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
]

const TYPE_OPTIONS: Array<{ value: ProductionType; label: string }> = [
  { value: 'festival', label: 'Festival' },
  { value: 'showcase', label: 'Showcase' },
  { value: 'film_screening', label: 'Film Screening' },
  { value: 'general', label: 'General' },
]

export function ProductionWizardPage() {
  const navigate = useNavigate()
  const { applications } = useAdminApplications()

  const [stepIdx, setStepIdx] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [autoSlug, setAutoSlug] = useState(true)

  const [values, setValues] = useState<ProductionFormValues>({
    name: '',
    slug: '',
    type: 'showcase',
    status: 'planning',
    venue: '',
    startDate: '',
    endDate: '',
    driveLink: '',
    intakeApplicationId: '',
    techApplicationId: '',
  })

  const update = <K extends keyof ProductionFormValues>(key: K, val: ProductionFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: val }))

  // Auto-slug from name unless the user has manually edited the slug
  useEffect(() => {
    if (!autoSlug) return
    setValues((v) => ({ ...v, slug: adminProductionsService.slugify(v.name) }))
  }, [values.name, autoSlug])

  const intakeApplications = useMemo(
    () =>
      applications.filter(
        (a) => a.applicationType === 'production_intake'
      ),
    [applications]
  )

  function nextStep() {
    setError(null)
    if (stepIdx === 0) {
      if (!values.name.trim()) return setError(withErrorCode('Name is required.', 'app/validation'))
      if (!values.slug.trim()) return setError(withErrorCode('Slug is required.', 'app/validation'))
      if (!/^[a-z0-9-]+$/.test(values.slug)) {
        return setError(withErrorCode('Slug may only contain lowercase letters, numbers, and hyphens.', 'app/validation'))
      }
    } else if (stepIdx === 1) {
      if (!values.venue.trim()) return setError(withErrorCode('Venue is required.', 'app/validation'))
      if (!values.startDate) return setError(withErrorCode('Start date is required.', 'app/validation'))
      if (values.endDate && values.endDate < values.startDate) {
        return setError(withErrorCode('End date cannot be earlier than start date.', 'app/validation'))
      }
    }
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1))
  }

  function prevStep() {
    setError(null)
    setStepIdx((i) => Math.max(i - 1, 0))
  }

  async function submit() {
    setError(null)
    setSubmitting(true)
    try {
      const id = await adminProductionsService.create(values)
      navigate(`/admin/productions/${id}`)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to create production.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-6">
      <div>
        <Link
          to="/admin/productions"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Productions
        </Link>
        <h1 className="text-2xl font-bold">New Production</h1>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((label, i) => (
          <div key={label} className="flex flex-1 items-center gap-2">
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                i <= stepIdx
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {i + 1}
            </div>
            <span
              className={`text-sm ${
                i === stepIdx ? 'font-semibold' : 'text-muted-foreground'
              }`}
            >
              {label}
            </span>
            {i < STEPS.length - 1 && <div className="ml-1 h-px flex-1 bg-border" />}
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-4 py-6">
          {stepIdx === 0 && (
            <>
              <div className="space-y-1">
                <Label htmlFor="name">Production Name</Label>
                <Input
                  id="name"
                  placeholder="SHIFT 2026"
                  value={values.name}
                  onChange={(e) => update('name', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="slug">URL Slug</Label>
                <Input
                  id="slug"
                  className="font-mono"
                  placeholder="shift-2026"
                  value={values.slug}
                  onChange={(e) => {
                    setAutoSlug(false)
                    update('slug', e.target.value)
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Will be used in future public ticket-sale URLs.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Type</Label>
                  <Select value={values.type} onValueChange={(v) => update('type', v as ProductionType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select
                    value={values.status}
                    onValueChange={(v) => update('status', v as ProductionStatus)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {stepIdx === 1 && (
            <>
              <div className="space-y-1">
                <Label htmlFor="venue">Venue</Label>
                <Input
                  id="venue"
                  placeholder="Joyce Theater"
                  value={values.venue}
                  onChange={(e) => update('venue', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={values.startDate}
                    onChange={(e) => update('startDate', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="endDate">End Date (optional)</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={values.endDate}
                    onChange={(e) => update('endDate', e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="driveLink">Google Drive Link (optional)</Label>
                <Input
                  id="driveLink"
                  placeholder="https://drive.google.com/..."
                  value={values.driveLink}
                  onChange={(e) => update('driveLink', e.target.value)}
                />
              </div>
            </>
          )}

          {stepIdx === 2 && (
            <>
              <div className="space-y-1">
                <Label>Intake Application</Label>
                <Select
                  value={values.intakeApplicationId || '__none__'}
                  onValueChange={(v) =>
                    update('intakeApplicationId', v === '__none__' ? '' : v)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Link to an existing intake application…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None (link later) —</SelectItem>
                    {intakeApplications.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only applications with type "Production Intake" appear here.
                </p>
              </div>
              {intakeApplications.length === 0 && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  <p className="mb-2">No production intake applications exist yet.</p>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/admin/applications/new" target="_blank" rel="noreferrer">
                      Create one in a new tab
                      <ExternalLink className="ml-1 h-3 w-3" />
                    </Link>
                  </Button>
                </div>
              )}
              <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
                <strong className="text-foreground">Review:</strong>{' '}
                {values.name} · {values.type} · {values.venue} ·{' '}
                {values.startDate || '(no start date)'}
                {values.endDate ? ` – ${values.endDate}` : ''}
              </div>
            </>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={prevStep} disabled={stepIdx === 0 || submitting}>
          <ChevronLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        {stepIdx < STEPS.length - 1 ? (
          <Button onClick={nextStep}>
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Create Production
          </Button>
        )}
      </div>
    </div>
  )
}
